import axios, { AxiosError, type AxiosRequestConfig } from 'axios';

/**
 * Kollez API client. Vite dev-proxy handles `/api` → localhost:5000 during dev.
 * In prod, `VITE_API_BASE_URL` is baked at build time (e.g. https://api.kollez.ai/api/v1).
 *
 * Auth model: access token lives in memory (never localStorage); refresh token in localStorage.
 * On 401, one refresh attempt is made using the stored refresh; on success the failed request
 * is retried once with the new access token.
 */

const BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const ACCESS_KEY = 'kollez.access';
const REFRESH_KEY = 'kollez.refresh';

let accessTokenMem: string | null = null;

export function setAccessToken(token: string | null) {
  accessTokenMem = token;
  // dev-only: persist so page reloads survive without a refresh call
  if (token) sessionStorage.setItem(ACCESS_KEY, token);
  else sessionStorage.removeItem(ACCESS_KEY);
}

export function getAccessToken(): string | null {
  if (accessTokenMem) return accessTokenMem;
  const cached = sessionStorage.getItem(ACCESS_KEY);
  if (cached) accessTokenMem = cached;
  return accessTokenMem;
}

export function setRefreshToken(token: string | null) {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export const api = axios.create({
  baseURL: BASE,
  timeout: 15_000,
  headers: { 'content-type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).authorization = `Bearer ${t}`;
  }
  return config;
});
// F-074: NEVER call tryRefresh from the REQUEST interceptor. `authStore.init()` triggers a
// refresh on boot, and any parallel request that also refreshes will race — server treats it
// as reuse and revokes the entire refresh-token family. Session dies. Only the RESPONSE
// interceptor may refresh, gated by the `refreshing` singleton promise.

let refreshing: Promise<string | null> | null = null;

export async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  refreshing = (async () => {
    try {
      const res = await axios.post(`${BASE}/auth/refresh`, { refreshToken });
      const tokens = res.data?.data?.tokens;
      if (tokens?.accessToken && tokens?.refreshToken) {
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);
        return tokens.accessToken as string;
      }
      return null;
    } catch (err) {
      // Only nuke the refresh token when the SERVER rejects it (401 = revoked or reuse-detected).
      // Transient network errors, timeouts, offline: keep the RT so the user isn't logged out
      // every time WiFi hiccups.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) {
        setAccessToken(null);
        setRefreshToken(null);
      }
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as AxiosRequestConfig & { _retried?: boolean } | undefined;
    if (
      err.response?.status === 401 &&
      original &&
      !original._retried &&
      !original.url?.endsWith('/auth/refresh')
    ) {
      original._retried = true;
      const newToken = await tryRefresh();
      if (newToken) {
        original.headers = { ...(original.headers ?? {}), authorization: `Bearer ${newToken}` };
        return api.request(original);
      }
    }
    return Promise.reject(err);
  },
);

/** Convenience wrapper — unwraps the Kollez `{ ok, data }` envelope. */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.get(url, config);
  return res.data?.data as T;
}
export async function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.post(url, body, config);
  return res.data?.data as T;
}
export async function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.patch(url, body, config);
  return res.data?.data as T;
}
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.delete(url, config);
  return res.data?.data as T;
}
